import FooterComponent from "@/components/common/layout/footer";
import Loader from "@/components/common/ui/loader";
import { useOrganizationContext, useSitesContext } from "@/context";
import { IBreadcrumb, useBreadcrumbContext } from "@/context/breadcrumb";
import {
  IDefaultLayoutV2Props,
  LayoutNextPage,
  V2_DEFAULT_LAYOUT_TYPE,
} from "@/types/layout";
import { SITE_INTEGRATION_TYPE } from "@shared/common/constant/site";
import { NextPage } from "next";
import { NextRouter, useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import PaymentAlert from "../common/payment-alert";
import Cafe24OnboardingModal from "../team/cafe24-onboarding-modal";
import MakeshopOnboardingModal from "../team/makeshop-onboarding-modal";
import NhnOnboardingModal from "../team/nhn-onboarding-modal";
import OnboardingModal from "../team/onboarding-modal";
import DefaultHeader from "./default-header";
import Sidebar from "./sidebar";

export interface IDefaultLayoutProps {
  Page: LayoutNextPage;
  layoutType: V2_DEFAULT_LAYOUT_TYPE;
  options?: {
    flexClass?: string;
    getBreadcrumbs?: (router: NextRouter) => IBreadcrumb[] | null;
  };
}

const DefaultLayout: React.FC<IDefaultLayoutProps> = (props) => {
  const [collapsed, setCollapsed] = useState(false);

  const organizationContext = useOrganizationContext();
  const sitesContext = useSitesContext();

  const {
    organizations,
    currentOrganization,
    isLoading: organizationLoading,
  } = organizationContext;
  const { currentSite, isLoading: siteLoading, reload, sites } = sitesContext;
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showCafe24Onboarding, setShowCafe24Onboarding] = useState(false);
  const [showNhnOnboarding, setShowNhnOnboarding] = useState(false);
  const [showMakeshopOnboarding, setShowMakeshopOnboarding] = useState(false);

  const { Page, layoutType } = props;
  const PageDefaultLayoutV2 = Page as NextPage<IDefaultLayoutV2Props>;
  const PageWithoutOrganization = Page as NextPage;

  const router = useRouter();

  const { setBreadcrumbs } = useBreadcrumbContext();
  useEffect(() => {
    setBreadcrumbs(props.options?.getBreadcrumbs?.(router) || null);
  }, [setBreadcrumbs, props.options, router]);

  useEffect(() => {
    if (siteLoading || organizationLoading) {
      return;
    }

    if (!sites || sites.length === 0) {
      setShowOnboarding(true);
    } else if (
      sites &&
      sites.length > 0 &&
      (currentOrganization?.onboardStep === 10 ||
        currentOrganization?.onboardStep === 30 ||
        currentOrganization?.onboardStep === 40)
    ) {
      // 사이트가 있는데 온보딩이 3~4단계라면
      setShowOnboarding(true);
    } else if (
      sites &&
      sites.length > 0 &&
      currentSite?.siteIntegration?.find(
        (integration) => integration.type === SITE_INTEGRATION_TYPE.CAFE24
      ) &&
      (currentOrganization?.onboardStep === 50 ||
        currentOrganization?.onboardStep === 60 ||
        currentOrganization?.onboardStep === 70)
    ) {
      // CAFE24 앱으로 접근하고 온보딩이 끝나지 않은 경우
      setShowCafe24Onboarding(true);
    } else if (
      sites &&
      sites.length > 0 &&
      currentSite?.siteIntegration?.find(
        (integration) => integration.type === SITE_INTEGRATION_TYPE.NHN
      ) &&
      (currentOrganization?.onboardStep === 50 ||
        currentOrganization?.onboardStep === 60 ||
        currentOrganization?.onboardStep === 70)
    ) {
      // CAFE24 앱으로 접근하고 온보딩이 끝나지 않은 경우
      setShowNhnOnboarding(true);
    } else if (
      sites &&
      sites.length > 0 &&
      currentSite?.siteIntegration?.find(
        (integration) => integration.type === SITE_INTEGRATION_TYPE.MAKESHOP
      ) &&
      (currentOrganization?.onboardStep === 50 ||
        currentOrganization?.onboardStep === 60 ||
        currentOrganization?.onboardStep === 70)
    ) {
      // CAFE24 앱으로 접근하고 온보딩이 끝나지 않은 경우
      setShowMakeshopOnboarding(true);
    } else {
      setShowOnboarding(false);
      setShowCafe24Onboarding(false);
      setShowNhnOnboarding(false);
      setShowMakeshopOnboarding(false);
    }
  }, [
    currentOrganization?.onboardStep,
    layoutType,
    siteLoading,
    sites,
    currentSite,
    organizationLoading,
  ]);

  const handleFinishAddSite = async () => {
    await reload();
    router.push("/v2");
    setTimeout(() => setShowOnboarding(false), 10);
  };

  if ((organizationLoading || siteLoading) && !showOnboarding) {
    if (layoutType !== "LOGO") {
      return <Loader size="large" />;
    }
  }

  return (
    <section
      id="contentSection"
      className={twMerge(
        "v2override",
        layoutType !== "LOGO" && "min-w-[1280px]",
        layoutType === V2_DEFAULT_LAYOUT_TYPE.HEADER && "layout-header"
      )}
    >
      {layoutType === "LOGO" ? (
        <DefaultHeader
          headerType="logo"
          collapsed={collapsed}
          setCollapsed={() => setCollapsed(!collapsed)}
        />
      ) : (
        <></>
      )}

      {layoutType !== "LOGO" ? (
        <section className={twMerge("flex w-full", props.options?.flexClass)}>
          {layoutType !== "HEADER" && (
            <Sidebar
              collapsed={collapsed}
              currentOrganization={currentOrganization || undefined}
            />
          )}

          <div className="min-w-0 grow">
            <DefaultHeader
              headerType="default"
              collapsed={collapsed}
              setCollapsed={() => setCollapsed(!collapsed)}
            />
            {/* 결제알림 알럿 */}
            <PaymentAlert />

            <div
              className={twMerge(
                "pb-6 pl-6 pr-6 text-base content-wrapper",
                layoutType === "HEADER" && "bg-cb-gray-50 pl-0 pr-0" // 회색 배경 영역
              )}
              style={{
                minHeight: "calc(100vh - var(--header-height))",
              }}
            >
              {organizations && currentOrganization ? (
                <PageDefaultLayoutV2
                  organizations={organizations}
                  currentOrganization={currentOrganization}
                  currentSite={currentSite}
                />
              ) : (
                <></>
              )}
            </div>
          </div>
        </section>
      ) : (
        <div className="px-5 pb-20 mt-16 md:mt-28 md:px-0 md:pb-28">
          <div className="flex flex-col items-center justify-center text-base">
            <PageWithoutOrganization />
          </div>
        </div>
      )}
      {layoutType === "DEFAULT" && organizations && currentOrganization ? (
        <>
          <OnboardingModal
            organizations={organizations}
            currentOrganization={currentOrganization}
            currentSite={currentSite}
            onComplete={handleFinishAddSite}
            reload={reload}
            showOnboarding={showOnboarding}
          />
          <Cafe24OnboardingModal
            organizations={organizations}
            currentOrganization={currentOrganization}
            currentSite={currentSite}
            onComplete={handleFinishAddSite}
            reload={reload}
            showOnboarding={showCafe24Onboarding}
          />
          <NhnOnboardingModal
            organizations={organizations}
            currentOrganization={currentOrganization}
            currentSite={currentSite}
            onComplete={handleFinishAddSite}
            reload={reload}
            showOnboarding={showNhnOnboarding}
          />
          <MakeshopOnboardingModal
            organizations={organizations}
            currentOrganization={currentOrganization}
            currentSite={currentSite}
            onComplete={handleFinishAddSite}
            reload={reload}
            showOnboarding={showMakeshopOnboarding}
          />
        </>
      ) : (
        <></>
      )}
      {process.env.NEXT_PUBLIC_PAYMENT_APP && (
        <div className="pt-[100px]">
          <FooterComponent />
        </div>
      )}
    </section>
  );
};

export const getDefaultLayout = (
  layoutType: V2_DEFAULT_LAYOUT_TYPE,
  options?: {
    flexClass?: string;
    getBreadcrumbs?: (router: NextRouter) => IBreadcrumb[] | null;
  }
) => {
  const siteLayout = (Page: LayoutNextPage, props: Record<string, unknown>) => (
    <DefaultLayout
      layoutType={layoutType}
      options={options}
      {...props}
      Page={Page}
    />
  );

  return siteLayout;
};

export default DefaultLayout;
